// MedicareCoverageSnapshot — the "10-second view" moonshot from the
// Medicare Intelligence Engine spec. One page answering everything
// an agent needs to know before opening a Medicare conversation.
//
// Only renders for leads whose vertical is medicare/senior. Hides
// itself for life/health leads.
//
// Data from /api/leads/medicare-snapshot which joins the drawer's
// Insurance Profile Medicare tab with client_medical_profile (mig
// 159) when a client_id exists.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const WINDOW_LABEL = {
  AEP:      { color: '#059669', bg: '#dcfce7', label: 'AEP OPEN' },
  OEP:      { color: '#059669', bg: '#dcfce7', label: 'OEP OPEN' },
  IEP:      { color: '#059669', bg: '#dcfce7', label: 'IEP OPEN' },
  SEP:      { color: '#0891b2', bg: '#cffafe', label: 'SEP CANDIDATE' },
  off_cycle:{ color: '#94a3b8', bg: '#f1f5f9', label: 'OFF CYCLE' },
};

function Row({ label, value, tone }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: '#64748b', minWidth: 120, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: 10, fontWeight: 700, paddingTop: 1 }}>
        {label}
      </span>
      <span style={{ color: tone || '#0f172a', fontWeight: 700 }}>{value}</span>
    </div>
  );
}

export default function MedicareCoverageSnapshot({ lead }) {
  // Accept either the top-level vertical OR the sector field. The Lead
  // form's Health → Medicare path saves { vertical:'health', sector:'medicare' },
  // and legacy paths save { vertical:'medicare' } directly. Missing this
  // dual check meant the Snapshot never rendered on real Medicare leads
  // created through the Health → Medicare flow (smoke test caught it).
  const vertical = String(lead?.vertical || '').toLowerCase();
  const sector   = String(lead?.sector   || '').toLowerCase();
  const isMedicare = vertical === 'medicare' || vertical === 'senior' || sector === 'medicare';
  const [state, setState] = React.useState({ loading: true, data: null, error: null });

  React.useEffect(() => {
    if (!isMedicare) return;
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const res = await authedFetchLead('/api/leads/medicare-snapshot', { lead }, controller.signal);
      if (controller.signal.aborted) return;
      setState({ loading: false, data: res.ok ? res.data : null, error: res.ok ? null : res.error });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, isMedicare]);

  // Diag tiles removed now that authedFetch.js guarantees the fetch
  // resolves (10s ceiling / 2s bearer watchdog). Hidden on non-
  // Medicare + loading is the intended silent-hide behavior.
  if (!isMedicare) return null;
  if (state.loading) return null;
  // Error is surfaced as a small amber pill so a genuine endpoint
  // failure is visible without cluttering the drawer.
  if (state.error) {
    return (
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 8,
      }}>
        Medicare Coverage Snapshot temporarily unavailable ({String(state.error)}).
      </div>
    );
  }
  if (!state.data) return null;
  const d = state.data;
  const w = WINDOW_LABEL[d.enrollment_window.kind] || WINDOW_LABEL.off_cycle;

  return (
    <div style={{
      background: '#ffffff',
      border: '2px solid #0284c7',
      borderRadius: 14,
      padding: 16,
    }}>
      {/* Header — dual eligible badge is the loudest signal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800 }}>
          🩺 Client Coverage Snapshot
        </div>
        <div style={{
          background: w.bg, color: w.color, border: `1px solid ${w.color}55`,
          padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
        }}>{w.label}</div>
        {d.coverage.dual_eligible && (
          <div style={{
            background: 'linear-gradient(135deg, #eab308 0%, #dc2626 100%)', color: '#ffffff',
            padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
            boxShadow: '0 4px 12px rgba(220,38,38,0.25)',
          }}>⭐ DUAL ELIGIBLE</div>
        )}
      </div>

      {/* Body — three columns of key facts */}
      <div style={{
        display: 'grid', gap: 16,
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      }}>
        {/* Medicare column */}
        <div>
          <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800, marginBottom: 6 }}>Medicare</div>
          <Row label="Part A" value={d.coverage.medicare.part_a_effective_at}/>
          <Row label="Part B" value={d.coverage.medicare.part_b_effective_at}/>
          <Row label="Part C" value={d.coverage.medicare.part_c_carrier}/>
          <Row label="Part D" value={d.coverage.medicare.part_d_carrier}/>
          <Row label="Medigap" value={d.coverage.medicare.medigap_carrier ? `${d.coverage.medicare.medigap_carrier}${d.coverage.medicare.medigap_plan_letter ? ' · Plan ' + d.coverage.medicare.medigap_plan_letter : ''}` : null}/>
          <Row label="Current plan" value={d.coverage.medicare.current_plan}/>
          <Row label="LIS" value={d.coverage.lis_status}/>
        </div>

        {/* Medicaid + Providers column */}
        <div>
          <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: d.coverage.medicaid.is_present ? '#dc2626' : '#64748b', fontWeight: 800, marginBottom: 6 }}>
            Medicaid {d.coverage.medicaid.is_present ? '· PRESENT' : '· NONE'}
          </div>
          <Row label="Status" value={d.coverage.medicaid.status} tone={d.coverage.medicaid.is_present ? '#dc2626' : undefined}/>
          <Row label="State" value={d.coverage.medicaid.state}/>
          <Row label="Renewal" value={d.coverage.medicaid.renewal_date}/>

          <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800, marginTop: 12, marginBottom: 6 }}>Providers</div>
          <Row label="Primary" value={d.provider_intelligence.primary_doctor}/>
          <Row label="Specialists" value={d.provider_intelligence.specialists}/>
          <Row label="Hospital" value={d.provider_intelligence.hospital}/>
          <Row label="Pharmacy" value={d.provider_intelligence.pharmacy}/>
          <Row label="On file" value={d.provider_intelligence.doctors_raw}/>
        </div>

        {/* Prescriptions + Compliance column */}
        <div>
          <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800, marginBottom: 6 }}>
            Prescriptions · {d.prescription_intelligence.count}
          </div>
          {d.prescription_intelligence.medications.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {d.prescription_intelligence.medications.slice(0, 8).map((m, i) => (
                <span key={i} style={{
                  fontSize: 10, background: '#eff6ff', color: '#1e40af',
                  border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 999, fontWeight: 700,
                }}>💊 {m}</span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 12 }}>None captured — ask on next call.</div>
          )}

          <div style={{ fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800, marginBottom: 6 }}>Compliance</div>
          {d.compliance_checklist.map(c => (
            <div key={c.key} style={{ display: 'flex', gap: 8, fontSize: 11, padding: '2px 0', color: '#334155' }}>
              <span style={{ color: c.status === 'pass' ? '#059669' : c.status === 'warn' ? '#d97706' : '#dc2626', fontWeight: 800 }}>
                {c.status === 'pass' ? '✓' : c.status === 'warn' ? '⚠' : '✗'}
              </span>
              <span style={{ flex: 1 }}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom strip — enrollment window details + open tasks */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #e2e8f0', display: 'grid', gap: 8 }}>
        <div style={{ fontSize: 11, color: '#475569' }}>
          <strong style={{ color: '#0369a1' }}>Enrollment window:</strong> {d.enrollment_window.details}
          {d.enrollment_window.days_remaining_in_window != null && (
            <span style={{ marginLeft: 6, color: '#dc2626', fontWeight: 800 }}>
              {d.enrollment_window.days_remaining_in_window}d left
            </span>
          )}
          {d.enrollment_window.days_until_next_window != null && (
            <span style={{ marginLeft: 6, color: '#94a3b8' }}>
              (next window in {d.enrollment_window.days_until_next_window}d)
            </span>
          )}
        </div>
        {d.next_review && (
          <div style={{ fontSize: 11, color: '#475569' }}>
            <strong style={{ color: '#0369a1' }}>Annual review:</strong>{' '}
            {d.next_review.days_until >= 0
              ? `Due in ${d.next_review.days_until}d`
              : `Overdue by ${Math.abs(d.next_review.days_until)}d`}
          </div>
        )}
        {d.open_tasks.length > 0 && (
          <div>
            <div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800, marginBottom: 4 }}>Open tasks</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {d.open_tasks.map((t, i) => (
                <span key={i} style={{
                  fontSize: 10, background: '#fff7ed', color: '#c2410c',
                  border: '1px solid #fed7aa', padding: '3px 10px', borderRadius: 999, fontWeight: 700,
                }}>{t.icon} {t.text}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
